import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, LogOut, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const empty = { name: "", description: "", category: "Букети", images: "", sizes: [{ label: "", price: "", dimensions: "" }], featured: false };
const STATUSES = ["нова", "потвърдена", "изпратена", "изпълнена", "отказана"];

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null); // product object or "new"
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const loadProducts = () => api.get("/products").then((r) => setProducts(r.data));
  const loadOrders = () => api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});

  useEffect(() => {
    loadProducts();
    loadOrders();
  }, []);

  const openNew = () => {
    setForm(empty);
    setEditing("new");
  };

  const openEdit = (p) => {
    setForm({
      name: p.name,
      description: p.description,
      category: p.category,
      images: (p.images || []).join("\n"),
      sizes: p.sizes?.length ? p.sizes.map((s) => ({ ...s, price: String(s.price) })) : [{ label: "", price: "", dimensions: "" }],
      featured: p.featured,
    });
    setEditing(p);
  };

  const save = async () => {
    const payload = {
      name: form.name,
      description: form.description,
      category: form.category,
      images: form.images.split("\n").map((s) => s.trim()).filter(Boolean),
      sizes: form.sizes
        .filter((s) => s.label && s.price !== "")
        .map((s) => ({ label: s.label, price: parseFloat(s.price), dimensions: s.dimensions || "" })),
      featured: form.featured,
    };
    if (!payload.name || payload.sizes.length === 0) {
      toast.error("Въведете име и поне един размер с цена");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") await api.post("/products", payload);
      else await api.put(`/products/${editing.id}`, payload);
      toast.success("Запазено");
      setEditing(null);
      loadProducts();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Изтриване на този продукт?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Изтрито");
    loadProducts();
  };

  const setStatus = async (id, status) => {
    await api.put(`/orders/${id}`, { status });
    setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
    toast.success("Статусът е обновен");
  };

  const setSize = (idx, key, val) =>
    setForm((f) => ({ ...f, sizes: f.sizes.map((s, i) => (i === idx ? { ...s, [key]: val } : s)) }));

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="border-b border-line bg-cream/90 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-[1200px] mx-auto px-6 h-20 flex items-center justify-between">
          <span className="font-serif text-2xl text-ink">Айлян<span className="text-clay">.</span> <span className="text-sm text-stone font-sans ml-2">Панел</span></span>
          <div className="flex items-center gap-3">
            <a href="/" className="text-sm text-stone hover:text-clay transition-colors">Виж сайта</a>
            <button
              onClick={() => { logout(); navigate("/admin/login"); }}
              className="inline-flex items-center gap-2 bg-ink text-cream px-5 py-2.5 rounded-full text-sm hover:bg-clay transition-colors"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" /> Изход
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-12">
        <Tabs defaultValue="products">
          <TabsList className="bg-sand mb-10">
            <TabsTrigger value="products" data-testid="tab-products">Продукти ({products.length})</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">Поръчки ({orders.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="products">
            <div className="flex justify-between items-center mb-8">
              <h2 className="font-serif text-3xl text-ink">Продукти</h2>
              <button
                onClick={openNew}
                className="inline-flex items-center gap-2 bg-clay text-cream px-5 py-2.5 rounded-full text-sm hover:bg-claydark transition-colors"
                data-testid="add-product-button"
              >
                <Plus className="w-4 h-4" /> Нов продукт
              </button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((p) => (
                <div key={p.id} className="border border-line rounded-xl overflow-hidden bg-sand" data-testid={`admin-product-${p.id}`}>
                  <img src={p.images?.[0]} alt={p.name} className="w-full h-48 object-cover" />
                  <div className="p-4">
                    <h3 className="font-serif text-xl text-ink">{p.name}</h3>
                    <p className="text-xs text-stone mt-1">{p.category} · {p.sizes?.length} размера</p>
                    <div className="flex gap-2 mt-4">
                      <button onClick={() => openEdit(p)} className="flex-1 inline-flex items-center justify-center gap-2 border border-line rounded-full py-2 text-sm hover:border-clay transition-colors" data-testid={`edit-${p.id}`}>
                        <Pencil className="w-3.5 h-3.5" /> Редактирай
                      </button>
                      <button onClick={() => remove(p.id)} className="px-3 border border-line rounded-full hover:border-destructive hover:text-destructive transition-colors" data-testid={`delete-${p.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="orders">
            <h2 className="font-serif text-3xl text-ink mb-8">Поръчки</h2>
            {orders.length === 0 ? (
              <p className="text-stone font-light">Все още няма поръчки.</p>
            ) : (
              <div className="space-y-4">
                {orders.map((o) => (
                  <div key={o.id} className="border border-line rounded-xl p-6 bg-sand" data-testid={`order-${o.id}`}>
                    <div className="flex flex-wrap justify-between gap-4 mb-4">
                      <div>
                        <h3 className="font-serif text-xl text-ink">{o.customer?.name}</h3>
                        <p className="text-sm text-stone">{o.customer?.phone} · {o.customer?.email || "без имейл"}</p>
                        {o.customer?.address && <p className="text-sm text-stone">{o.customer.address}</p>}
                        {o.customer?.note && <p className="text-sm text-stone italic mt-1">„{o.customer.note}“</p>}
                      </div>
                      <div className="text-right">
                        <span className="font-serif text-2xl text-ink">{o.total} лв</span>
                        <div className="mt-2 w-40">
                          <Select value={o.status} onValueChange={(v) => setStatus(o.id, v)}>
                            <SelectTrigger className="bg-cream border-line" data-testid={`order-status-${o.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUSES.map((s) => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                    <div className="border-t border-line pt-3 space-y-1">
                      {o.items?.map((it, i) => (
                        <div key={i} className="flex justify-between text-sm text-stone">
                          <span>{it.name} · {it.size_label} × {it.qty}</span>
                          <span>{it.price * it.qty} лв</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg bg-cream border-line max-h-[90vh] overflow-y-auto" data-testid="product-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-ink">
              {editing === "new" ? "Нов продукт" : "Редактиране"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Име">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="form-name" />
            </Field>
            <Field label="Описание">
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className={inputCls + " resize-none"} data-testid="form-description" />
            </Field>
            <Field label="Категория">
              <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} data-testid="form-category" />
            </Field>
            <Field label="Изображения (по един URL на ред)">
              <textarea value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} rows={2} className={inputCls + " resize-none text-xs"} data-testid="form-images" />
            </Field>
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-xs uppercase tracking-[0.15em] text-stone">Размери и цени</label>
                <button onClick={() => setForm((f) => ({ ...f, sizes: [...f.sizes, { label: "", price: "", dimensions: "" }] }))} className="text-clay text-sm inline-flex items-center gap-1" data-testid="add-size">
                  <Plus className="w-3.5 h-3.5" /> Размер
                </button>
              </div>
              <div className="space-y-2">
                {form.sizes.map((s, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <input placeholder="Размер" value={s.label} onChange={(e) => setSize(idx, "label", e.target.value)} className={inputCls + " flex-1"} data-testid={`size-label-${idx}`} />
                    <input placeholder="Цена" type="number" value={s.price} onChange={(e) => setSize(idx, "price", e.target.value)} className={inputCls + " w-24"} data-testid={`size-price-${idx}`} />
                    <input placeholder="Размери" value={s.dimensions} onChange={(e) => setSize(idx, "dimensions", e.target.value)} className={inputCls + " w-24"} />
                    <button onClick={() => setForm((f) => ({ ...f, sizes: f.sizes.filter((_, i) => i !== idx) }))} className="text-stone hover:text-destructive">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <button
              onClick={save}
              disabled={saving}
              className="w-full bg-ink text-cream py-3.5 rounded-full text-sm font-medium hover:bg-clay transition-colors disabled:opacity-60"
              data-testid="save-product-button"
            >
              {saving ? "Запазване…" : "Запази"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const inputCls = "w-full bg-sand border border-line rounded-lg px-3 py-2.5 text-ink outline-none focus:border-clay transition-colors text-sm";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.15em] text-stone block mb-2">{label}</label>
      {children}
    </div>
  );
}
